import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { ShoppingCart as POIcon, Add as AddIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import purchaseOrderService from '../../../../services/purchaseOrderService';
import supplierService from '../../../../services/supplierService';
import POStatusBadge from '../../../../components/venue/POStatusBadge';
import type { PurchaseOrder, PurchaseOrderStatus, Supplier } from '../../../../types/venue';

const STATUS_OPTIONS: { value: PurchaseOrderStatus | ''; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'ordered', label: 'Ordered' },
  { value: 'partially_received', label: 'Partially Received' },
  { value: 'received', label: 'Received' },
  { value: 'closed', label: 'Closed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const PurchaseOrderList: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { venueId } = useVenue();
  const base = `/admin/venues/${venueId}/purchasing`;

  // ── Filters ──────────────────────────────────────────────────────────────────
  const [statusFilter, setStatusFilter] = useState<PurchaseOrderStatus | ''>('');

  // ── Data ─────────────────────────────────────────────────────────────────────
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [supplierMap, setSupplierMap] = useState<Map<string, string>>(new Map());

  // ── Load suppliers for name lookup ───────────────────────────────────────────
  useEffect(() => {
    supplierService.getAllSuppliers(1, 500).then(result => {
      setSupplierMap(new Map(result.data.map((s: Supplier) => [s._id, s.name])));
    }).catch(() => { /* non-blocking */ });
  }, []);

  // ── Load orders ───────────────────────────────────────────────────────────────
  const loadOrders = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await purchaseOrderService.getOrders(
        venueId,
        page + 1,
        pageSize,
        statusFilter || undefined,
      );
      setOrders(result.data ?? []);
      setTotalItems(result.pagination?.totalItems ?? 0);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  }, [venueId, page, pageSize, statusFilter]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  const handleStatusChange = (value: string) => {
    setStatusFilter(value as PurchaseOrderStatus | '');
    setPage(0);
  };

  const formatDate = (iso?: string) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <POIcon sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
          <Typography variant="h5" fontWeight={700}>Purchase Orders</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate(`${base}/create`)}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          New Order
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={statusFilter}
            onChange={e => handleStatusChange(e.target.value)}
          >
            {STATUS_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {['Order ID', 'Supplier', 'Status', 'Ordered', 'Expected Delivery', 'Created'].map(h => (
                  <TableCell
                    key={h}
                    sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: '0.72rem', textTransform: 'uppercase', py: 1.5 }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box sx={{ py: 6, textAlign: 'center', color: theme.palette.text.secondary }}>
                      <POIcon sx={{ fontSize: 44, mb: 1, opacity: 0.25 }} />
                      <Typography variant="body2">No purchase orders found.</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                orders.map(order => (
                  <TableRow
                    key={order._id}
                    hover
                    onClick={() => navigate(`${base}/${order._id}`)}
                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) } }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} fontFamily="monospace" fontSize="0.78rem">
                        {order._id.slice(-8).toUpperCase()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {supplierMap.get(order.supplierId) ?? <span style={{ color: theme.palette.text.disabled }}>—</span>}
                      </Typography>
                    </TableCell>
                    <TableCell><POStatusBadge status={order.status} /></TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{formatDate(order.orderedAt)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{formatDate(order.expectedDeliveryDate)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{formatDate(order.createdAt)}</Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalItems}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={e => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[20, 50, 100]}
        />
      </Paper>
    </Box>
  );
};

export default PurchaseOrderList;
